// src/LeaveManagementPage.js - VERSIONE COMPLETA CON MODIFICA NOTE

import React, { useState, useEffect } from 'react';
import { db, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, onSnapshot, doc, updateDoc, orderBy, getDocs, deleteDoc, addDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { useUser } from './UserContext';
import moment from 'moment';
import './LeaveManagement.css';
import { LeaveRequestModal } from './LeaveRequestModal';
import { ConfirmationModal } from './Modals';
import { createNotification } from './notificationService';
import { toast } from 'react-toastify';
import { logAction } from './loggingService';

const processLeaveApproval = httpsCallable(functions, 'processLeaveApproval');
const cancelLeaveRequest = httpsCallable(functions, 'cancelLeaveRequest');

function LeaveManagementPage() {
    const { userProfile } = useUser();
    const permissionLevel = userProfile?.permissions?.leaves || 'none';
    const isManager = permissionLevel === 'completo' || permissionLevel === 'semi-completo';

    const [allStaffData, setAllStaffData] = useState([]);
    const [requests, setRequests] = useState([]);
    // 🆕 Richieste dedicate al calcolo dei saldi (Ferie/ROL residuo).
    // Indipendente da 'requests', che invece contiene solo i documenti
    // del tab attualmente selezionato (pending/approved/denied) e quindi
    // non è affidabile per calcoli che devono vedere sia le approvate
    // sia le pending contemporaneamente, a prescindere dal tab aperto.
    const [balanceRequests, setBalanceRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [leaveModalData, setLeaveModalData] = useState(null);
    const [activeTab, setActiveTab] = useState(isManager ? 'pending' : 'approved');
    const [searchTerm, setSearchTerm] = useState('');
    
    // --- NUOVI STATI PER LA MODIFICA NOTE ---
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [tempNoteText, setTempNoteText] = useState("");
    const [isSavingNote, setIsSavingNote] = useState(false);
    // ----------------------------------------

    const [personalBalances, setPersonalBalances] = useState({ 
        vacationTaken: 0, 
        rolTaken: 0, 
        sicknessTaken: 0, 
        permessi104Taken: 0 
    });
    const [searchedUserBalances, setSearchedUserBalances] = useState(null);
    // 🆕 Filtro data per la statistica "Goduto" (Ferie e ROL). Di default
    // vuoto = conteggio cumulativo di sempre. Se valorizzato, restringe
    // il conteggio all'intervallo scelto.
    const [balanceDateFilter, setBalanceDateFilter] = useState({ from: '', to: '' });
    const [cancelModal, setCancelModal] = useState({ isOpen: false, request: null });
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, request: null });

    // Fetch Staff (Solo Manager)
    // 🐛 FIX: prima era un getDocs "una tantum" al montaggio della pagina.
    // Il saldo ferie/ROL (leaveBalances) cambia continuamente grazie alle
    // CF di approvazione/storno, ma questa lista non veniva mai
    // riaggiornata: si vedeva sempre il saldo di quando la pagina era
    // stata aperta, mai quello reale dopo un'approvazione. Ora usiamo
    // onSnapshot, come già facciamo per 'requests', così resta live.
    useEffect(() => {
        if (isManager && userProfile && userProfile.centerId) {
            const staffRef = collection(db, 'centers', userProfile.centerId, 'staff');
            const unsubscribe = onSnapshot(staffRef, (snapshot) => {
                const staffList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setAllStaffData(staffList);
            });
            return () => unsubscribe();
        }
    }, [isManager, userProfile]);

    // 🆕 Listener sempre attivo (approved + pending), usato SOLO per
    // calculateNetBalance. Serve a vedere sempre il quadro completo dei
    // saldi in tempo reale, senza dipendere da quale tab l'utente ha
    // aperto in quel momento nella UI.
    useEffect(() => {
        if (!userProfile || !userProfile.centerId) return;
        const requestsRef = collection(db, 'centers', userProfile.centerId, 'leaveRequests');
        const q = isManager
            ? query(requestsRef, where('status', 'in', ['approved', 'pending']))
            : query(requestsRef, where('userId', '==', userProfile.uid), where('status', 'in', ['approved', 'pending']));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setBalanceRequests(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => unsubscribe();
    }, [isManager, userProfile]);
    
    // Calcolo Saldi Personali
    useEffect(() => {
        if (!userProfile || !userProfile.centerId) return;

        const currentYear = moment().year();
        const q = query(
            collection(db, 'centers', userProfile.centerId, 'leaveRequests'),
            where('userId', '==', userProfile.uid),
            where('status', '==', 'approved')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            let vacationDaysThisYear = 0;
            let rolHoursThisYear = 0;
            let sicknessDaysThisYear = 0;
            let permessi104HoursThisYear = 0;

            const weeklyHours = userProfile.contractData?.weeklyHours || 40;
            const hoursPerDay = weeklyHours > 0 ? weeklyHours / 5 : 8;

            snapshot.docs.forEach(doc => {
                const request = doc.data();
                const reqStart = moment(request.startDate);
                const reqEnd = moment(request.endDate);

                let daysInCurrentYear = 0;
                for (let day = reqStart.clone(); day.isSameOrBefore(reqEnd); day.add(1, 'day')) {
                    if (day.year() === currentYear && day.isoWeekday() < 6) {
                       daysInCurrentYear++;
                    }
                }

                if (daysInCurrentYear > 0) {
                    if (request.type === 'Ferie') {
                        vacationDaysThisYear += daysInCurrentYear;
                    } else if (request.type === 'Malattia') {
                        sicknessDaysThisYear += daysInCurrentYear;
                    } else if (request.type === 'Permesso retribuito (ROL)' || request.type === 'Permesso ROL') {
                        if (request.leaveHours) {
                            if (reqStart.year() === currentYear) {
                                rolHoursThisYear += (request.leaveHours.h || 0) + ((request.leaveHours.m || 0) / 60);
                            }
                        } else {
                            rolHoursThisYear += daysInCurrentYear * hoursPerDay;
                        }
                    } 
                    else if (request.type === 'Permesso 104') {
                        if (request.leaveHours) {
                            if (reqStart.year() === currentYear) {
                                permessi104HoursThisYear += (request.leaveHours.h || 0) + ((request.leaveHours.m || 0) / 60);
                            }
                        } else {
                            permessi104HoursThisYear += daysInCurrentYear * hoursPerDay;
                        }
                    }
                }
            });

            setPersonalBalances({
                vacationTaken: vacationDaysThisYear,
                rolTaken: rolHoursThisYear,
                sicknessTaken: sicknessDaysThisYear,
                permessi104Taken: permessi104HoursThisYear
            });
        });

        return () => unsubscribe();
    }, [userProfile]);

   // Caricamento Lista Richieste
   useEffect(() => {
        if (!userProfile || !userProfile.centerId) {
            setIsLoading(false);
            setRequests([]);
            return;
        }

        const requestsRef = collection(db, 'centers', userProfile.centerId, 'leaveRequests');
        let q;

        if (isManager) {
            q = query(requestsRef, where('status', '==', activeTab), orderBy('startDate', 'asc'));
        } else {
            q = query(requestsRef, 
                where('userId', '==', userProfile.uid), 
                where('status', '==', activeTab),
                orderBy('startDate', 'asc')
            );
        }

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setRequests(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setIsLoading(false);
        }, (error) => {
            console.error("Errore nello snapshot listener:", error);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [isManager, userProfile, activeTab]);
    
    // Logica Ricerca Utente (Manager)
    useEffect(() => {
        if (isManager && userProfile.centerId && searchTerm.trim() !== '' && allStaffData.length > 0) {
            const lowerCaseSearch = searchTerm.toLowerCase();
            const foundUser = allStaffData.find(staff => staff.name.toLowerCase().includes(lowerCaseSearch));

            if (foundUser) {
                const yearStart = moment().startOf('year');
                const yearEnd = moment().endOf('year');
                
                const q = query(collection(db, 'centers', userProfile.centerId, 'leaveRequests'), where('userId', '==', foundUser.id), where('status', '==', 'approved'));
                const unsubscribe = onSnapshot(q, (snapshot) => {
                    let vacation = 0, rolHours = 0, sickness = 0, p104Hours = 0;
                    const weeklyHours = foundUser.contractData?.weeklyHours || 40;
                    const hoursPerDay = weeklyHours > 0 ? weeklyHours / 5 : 8;

                    snapshot.docs.forEach(doc => {
                        const req = doc.data();
                        if (moment(req.startDate).isBetween(yearStart, yearEnd, 'day', '[]')) {
                            if (req.type === 'Ferie') vacation += req.totalDays;
                            else if (req.type === 'Permesso retribuito (ROL)' || req.type === 'Permesso ROL') {
                                if (req.leaveHours) {
                                    rolHours += (req.leaveHours.h || 0) + ((req.leaveHours.m || 0) / 60);
                                } else {
                                    rolHours += req.totalDays * hoursPerDay;
                                }
                            }
                            else if (req.type === 'Permesso 104') {
                                if (req.leaveHours) {
                                    p104Hours += (req.leaveHours.h || 0) + ((req.leaveHours.m || 0) / 60);
                                } else {
                                    p104Hours += req.totalDays * hoursPerDay;
                                }
                            }
                            else if (req.type === 'Malattia') sickness += req.totalDays;
                        }
                    });

                    setSearchedUserBalances({
                        user: foundUser,
                        vacationTaken: vacation,
                        rolTaken: rolHours,
                        sicknessTaken: sickness,
                        permessi104Taken: p104Hours
                    });
                });
                return () => unsubscribe();
            } else {
                setSearchedUserBalances(null);
            }
        } else {
            setSearchedUserBalances(null);
        }
    }, [searchTerm, isManager, allStaffData, userProfile]);

    // --- FUNZIONI MODIFICA NOTE ---
    const handleStartEditingNote = (request) => {
        // Avvia la modifica popolando il testo temporaneo
        setEditingNoteId(request.id);
        setTempNoteText(request.notes || "");
    };

    const handleNoteBlur = async (request) => {
        // Salva quando si clicca fuori (onBlur)
        if (tempNoteText === (request.notes || "")) {
            // Nessuna modifica, chiudi solo l'editor
            setEditingNoteId(null);
            return;
        }

        setIsSavingNote(true);
        try {
            const requestRef = doc(db, 'centers', userProfile.centerId, 'leaveRequests', request.id);
            await updateDoc(requestRef, {
                notes: tempNoteText.trim()
            });
            toast.success("Nota aggiornata!");
        } catch (error) {
            console.error("Errore salvataggio nota:", error);
            toast.error("Errore durante il salvataggio della nota.");
        } finally {
            setIsSavingNote(false);
            setEditingNoteId(null);
        }
    };
    // ------------------------------

    const handleCancelRequest = async (requestId) => {
        const requestToCancel = requests.find(r => r.id === requestId);
        try {
            await toast.promise(
                cancelLeaveRequest({ requestId: requestId, centerId: userProfile.centerId }),
                {
                    pending: 'Annullamento in corso...',
                    success: 'Richiesta annullata e calendario aggiornato!',
                    error: 'Si è verificato un errore durante l\'annullamento.'
                }
            );
            
            if (requestToCancel) {
                 await logAction(userProfile, `ha annullato la propria richiesta di ${requestToCancel.type}`);
            }
        } catch (error) {
            console.error("Errore frontend durante l'annullamento:", error);
        } finally {
            setCancelModal({ isOpen: false, request: null });
        }
    };

    const handleDeleteApprovedRequest = async (requestId) => {
        const requestToDelete = requests.find(r => r.id === requestId);
        try {
            await toast.promise(
                cancelLeaveRequest({ requestId: requestId, centerId: userProfile.centerId }),
                {
                    pending: 'Eliminazione e ripristino in corso...',
                    success: 'Assenza eliminata e calendario aggiornato!',
                    error: 'Si è verificato un errore durante l\'eliminazione.'
                }
            );

            if (requestToDelete) {
                 await logAction(userProfile, `ha eliminato la richiesta approvata di ${requestToDelete.type} per ${requestToDelete.userName}`);
            }
        } catch (error) {
            console.error("Errore frontend durante l'eliminazione:", error);
        } finally {
            setDeleteModal({ isOpen: false, request: null });
        }
    };

    const handleLeaveRequestSubmit = async (requestData) => {
        const leavesPermission = leaveModalData.effectivePermissionLevel || (userProfile.permissions?.leaves || 'none');
        const isManager = leavesPermission !== 'self-edit';
        const targetPersonId = leaveModalData.personId;
        const isRequestForSelf = targetPersonId === userProfile.uid;
        const isFullManager = leavesPermission === 'completo';
        const isSemiManager = leavesPermission === 'semi-completo';
        
        let targetProfile = allStaffData.find(p => p.id === targetPersonId); 
        
        if (!targetProfile && (targetPersonId === userProfile.uid || targetPersonId === userProfile.id)) {
            targetProfile = userProfile;
        }
    
        if (!targetProfile) {
             toast.error("Errore: Profilo utente non trovato per la richiesta.");
             setLeaveModalData(null);
             return;
        }
        const totalDays = moment(requestData.endDate).diff(moment(requestData.startDate), 'days') + 1;
        let status = 'pending'; 
        const isPastDate = moment(requestData.endDate).isBefore(moment(), 'day');
    
        if (
            requestData.type === 'Malattia' || 
            requestData.type === 'Permesso 104' ||
            isFullManager || 
            (isSemiManager && !isRequestForSelf) || 
            (isManager && requestData.type.includes('Permesso')) || 
            (permissionLevel === 'self-edit' && isRequestForSelf && requestData.type.includes('Permesso') && isPastDate)
        ) {
            status = 'approved';
        }
    
        try {
            const start = moment(requestData.startDate);
            const end = moment(requestData.endDate);
            const personId = leaveModalData.personId; 
            const personName = leaveModalData.personName; 

            // Recupero turni originali
            let originalShifts = {}; 
            const affectedMonths = new Set(); 
            for (let day = start.clone(); day.isSameOrBefore(end); day.add(1, 'day')) {
                affectedMonths.add(day.format("YYYY-MM"));
            }

            for (const monthId of affectedMonths) {
                const scheduleDocRef = doc(db, 'centers', userProfile.centerId, 'schedules', monthId); 
                const scheduleDoc = await getDoc(scheduleDocRef); 

                if (scheduleDoc.exists()) {
                    const scheduleData = JSON.parse(scheduleDoc.data().schedule || "{}");
                    for (let day = start.clone(); day.isSameOrBefore(end); day.add(1, 'day')) {
                        if (day.format("YYYY-MM") === monthId) {
                            const dayStr = day.format("YYYY-MM-DD");
                            originalShifts[dayStr] = scheduleData[dayStr]?.[personId] || scheduleData[dayStr]?.[personName] || {};
                        }
                    }
                }
            }

            const requestPayload = {
              userId: personId, 
              userName: personName, 
              type: requestData.type,
              startDate: requestData.startDate, 
              endDate: requestData.endDate, 
              totalDays: totalDays,
              notes: requestData.notes.trim(), 
              status: status, 
              requestedAt: serverTimestamp(),
              originalShifts: originalShifts,
              centerId: userProfile.centerId,
            };

            if (requestData.type.includes("Permesso") || requestData.type === 'Permesso 104') {
              requestPayload.leaveHours = requestData.leave || { h: 0, m: 0 };
            }

            const newRequestRef = await addDoc(collection(db, 'centers', userProfile.centerId, 'leaveRequests'), requestPayload);

            if (status === 'approved') {
                toast.promise(
                    processLeaveApproval({ requestId: newRequestRef.id, centerId: userProfile.centerId }),
                    {
                      pending: 'Approvazione automatica in corso...',
                      success: 'Calendario e contatori aggiornati!',
                      error: "Errore durante l'aggiornamento dei contatori."
                    }
                ).catch(cfError => console.error(`Errore da processLeaveApproval`, cfError));
                
                if (isManager && !isRequestForSelf) {
                    createNotification(personId, userProfile, `ha inserito e approvato ${requestData.type} per te.`, '/assenze');
                }
                await logAction(userProfile, `ha inserito e approvato ${requestData.type} per ${personName}`);

            } else { 
                const staffRef = collection(db, 'centers', userProfile.centerId, 'staff');
                const q = query(staffRef, where("permissions.leaves", "in", ["completo", "semi-completo"]));
                const querySnapshot = await getDocs(q);
                querySnapshot.forEach(doc => {
                  if (doc.id !== userProfile.uid) { 
                    createNotification(doc.id, userProfile, `ha inviato una nuova richiesta di ${requestData.type}.`, '/assenze');
                  }
                });
                createNotification(personId, { name: "Sistema", uid: "system" }, `La tua richiesta di ${requestData.type} è in attesa di approvazione.`, '/assenze');
                await logAction(userProfile, `ha richiesto ${requestData.type} per ${personName}`);
            }

            toast.success("Richiesta inviata con successo!"); 
            setLeaveModalData(null); 

        } catch (err) { 
            console.error("Errore generale in handleLeaveRequestSubmit: ", err);
            toast.error(`Si è verificato un errore: ${err.message || 'Riprova.'}`);
            setLeaveModalData(null);
        }
    };

    const handleUpdateRequestStatus = async (request, newStatus) => {
        const requestDocRef = doc(db, 'centers', userProfile.centerId, 'leaveRequests', request.id); 
        try {
            await updateDoc(requestDocRef, { status: newStatus });

            const q = query(collection(db, 'centers', userProfile.centerId, "staff"), where("name", "==", request.userName)); 
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
                const recipientId = querySnapshot.docs[0].id;
                const statusText = newStatus === 'approved' ? 'approvata' : 'rifiutata';
                await createNotification(
                    recipientId, userProfile,
                    `La tua richiesta di ${request.type} è stata ${statusText}.`,
                    '/assenze'
                );
            }

            if (newStatus === 'denied') {
                await toast.promise(
                    cancelLeaveRequest({ requestId: request.id, centerId: userProfile.centerId }), 
                    {
                        pending: 'Ripristino calendario in corso...',
                        success: 'Richiesta rifiutata e calendario aggiornato!',
                        error: 'Errore durante il ripristino del calendario.'
                    }
                );
                 await logAction(userProfile, `ha rifiutato la richiesta di ${request.type} per ${request.userName}`);

            } else if (newStatus === 'approved') {
                toast.promise(
                    processLeaveApproval({ requestId: request.id, centerId: userProfile.centerId }), 
                    {
                      pending: 'Elaborazione automatica in corso...',
                      success: 'Contatori e calendario aggiornati!',
                      error: 'Errore durante l\'elaborazione automatica.'
                    }
                );
                await logAction(userProfile, `ha approvato la richiesta di ${request.type} per ${request.userName}`);
            }
        } catch (error) {
            console.error("Errore durante l'aggiornamento dello stato:", error);
            toast.error("Si è verificato un errore durante l'aggiornamento.");
        }
    };
    
    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending': return <span className="status-badge pending">In Attesa</span>;
            case 'approved': return <span className="status-badge approved">Approvata</span>;
            case 'denied': return <span className="status-badge denied">Rifiutata</span>;
            default: return null;
        }
    };
    
    const filteredRequests = isManager 
        ? requests.filter(req => req.userName.toLowerCase().includes(searchTerm.toLowerCase()))
        : requests;

    const calculateNetBalance = (profile, allRequests, dateFilter) => {
        // 'vacationBalance'/'rolBalance' sono il saldo NETTO: le CF lo
        // scalano ad ogni approvazione (passata O futura, non importa la
        // data) e lo ripristinano ad ogni annullamento/storno. Non serve
        // sottrarre altro: è già il residuo corretto.
        const vacationBalance = profile.leaveBalances?.vacationBalance ?? 0;
        const rolBalance = profile.leaveBalances?.rolBalance ?? 0;

        const today = moment().startOf('day');

        // 🆕 Statistica "Goduto": di default CUMULATIVA (tutta la storia
        // di sempre, non più limitata all'anno solare corrente). Se viene
        // passato un intervallo (dateFilter.from/to, dai selettori sopra
        // le card), restringiamo il conteggio a quel periodo specifico.
        const hasDateFilter = !!(dateFilter && (dateFilter.from || dateFilter.to));
        const filterFrom = dateFilter?.from ? moment(dateFilter.from).startOf('day') : null;
        const filterTo = dateFilter?.to ? moment(dateFilter.to).endOf('day') : null;

        const approvedInRange = allRequests.filter(req => {
            if (req.userId !== profile.id || req.status !== 'approved') return false;
            if (!hasDateFilter) return true; // cumulativo: nessun limite di data
            const sd = moment(req.startDate?.toDate ? req.startDate.toDate() : req.startDate);
            if (filterFrom && sd.isBefore(filterFrom)) return false;
            if (filterTo && sd.isAfter(filterTo)) return false;
            return true;
        });

        const vacationTaken = approvedInRange
            .filter(r => r.type === 'Ferie')
            .reduce((sum, r) => sum + (r.totalDays || 0), 0);

        const rolTaken = approvedInRange
            .filter(r => ['Permesso ROL', 'Permesso retribuito (ROL)'].includes(r.type))
            .reduce((sum, r) => {
                if (r.leaveHours && (r.leaveHours.h > 0 || r.leaveHours.m > 0)) {
                    return sum + (r.leaveHours.h || 0) + ((r.leaveHours.m || 0) / 60);
                }
                const wh = profile.contractData?.weeklyHours || 40;
                return sum + (r.totalDays || 0) * (wh > 0 ? wh / 5 : 8);
            }, 0);

        // 🐛 FIX: "future programmate" deve mostrare le Ferie/ROL GIÀ
        // APPROVATE con data futura (es. le ferie di martedì prossimo che
        // hai già approvato), non le richieste ancora in attesa. Sono già
        // scalate dal saldo (vacationBalance le contiene già): qui
        // servono solo a scopo informativo, non le sottraiamo di nuovo.
        const approvedFuture = allRequests.filter(req => {
            if (req.userId !== profile.id || req.status !== 'approved') return false;
            const sd = moment(req.startDate?.toDate ? req.startDate.toDate() : req.startDate);
            return sd.isSameOrAfter(today, 'day');
        });

        const futureVacationCost = approvedFuture
            .filter(r => r.type === 'Ferie')
            .reduce((sum, r) => sum + (r.totalDays || 0), 0);

        const futureRolCost = approvedFuture
            .filter(r => ['Permesso ROL', 'Permesso retribuito (ROL)'].includes(r.type))
            .reduce((sum, r) => {
                if (r.leaveHours) return sum + (r.leaveHours.h || 0) + ((r.leaveHours.m || 0) / 60);
                return sum;
            }, 0);

        return {
            vacationRemaining: vacationBalance,
            vacationTaken,
            rolRemaining: rolBalance,
            rolTaken,
            futureVacationCost,
            futureRolCost,
        };
    };

    // VISTA PERSONALE
    const myNetStats = calculateNetBalance({ id: userProfile.uid, ...userProfile }, balanceRequests, balanceDateFilter);
    const myVacationRemaining = myNetStats.vacationRemaining;
    const myVacationTaken = myNetStats.vacationTaken;
    const myRolRemaining = myNetStats.rolRemaining;
    const myRolTaken = myNetStats.rolTaken;

    // VISTA MANAGER
    let searchedVacationRemaining = 0, searchedVacationTaken = 0;
    let searchedRolRemaining = 0, searchedRolTaken = 0;
    let searchedFutureVacation = 0; 

    if (searchedUserBalances) {
        const u = searchedUserBalances.user;
        const searchedStats = calculateNetBalance(u, balanceRequests, balanceDateFilter);
        
        searchedVacationRemaining = searchedStats.vacationRemaining;
        searchedVacationTaken = searchedStats.vacationTaken;
        searchedRolRemaining = searchedStats.rolRemaining;
        searchedRolTaken = searchedStats.rolTaken;
        searchedFutureVacation = searchedStats.futureVacationCost;
    }

    return (
        <div className="leave-container">
            {leaveModalData && (
                <LeaveRequestModal
                    onClose={() => setLeaveModalData(null)}
                    personId={leaveModalData.personId}
                    personName={leaveModalData.personName}
                    personProfileData={leaveModalData.personProfileData}
                    selectedDate={leaveModalData.selectedDate}
                    leaveType={leaveModalData.leaveType}
                    effectivePermissionLevel={leaveModalData.effectivePermissionLevel}
                    onSubmit={handleLeaveRequestSubmit}
                />
            )}
            
            {cancelModal.isOpen && (
                <ConfirmationModal
                    title="Conferma Annullamento"
                    onConfirm={() => handleCancelRequest(cancelModal.request.id)}
                    onClose={() => setCancelModal({ isOpen: false, request: null })}
                >
                    <p>Annullare richiesta di {cancelModal.request.type}?</p>
                </ConfirmationModal>
            )}
            {deleteModal.isOpen && (
                <ConfirmationModal
                    title="Conferma Eliminazione"
                    onConfirm={() => handleDeleteApprovedRequest(deleteModal.request.id)}
                    onClose={() => setDeleteModal({ isOpen: false, request: null })}
                >
                    <p>Eliminare assenza di <strong>{deleteModal.request.userName}</strong>?</p>
                </ConfirmationModal>
            )}

            <div className="leave-header">
                <h1>Gestione Assenze</h1>
                <button className="new-request-btn" onClick={() => {
                    const targetUser = searchedUserBalances ? searchedUserBalances.user : userProfile;
                    const targetId = targetUser.uid || targetUser.id; 
                    setLeaveModalData({
                        personId: targetId, 
                        personName: targetUser.name,
                        personProfileData: targetUser,
                        selectedDate: moment().format('YYYY-MM-DD'),
                        leaveType: 'Ferie',
                        effectivePermissionLevel: permissionLevel
                    });
                }}>+ Nuova Richiesta</button>
            </div>

            {/* 🆕 Filtro data per la statistica "Goduto" (Ferie/ROL).
                Vuoto = cumulativo di sempre. Si applica sia alla vista
                personale sia a quella dell'utente cercato, dato che
                'calculateNetBalance' è condivisa tra i due casi. */}
            <div style={{
                display: 'flex', alignItems: 'flex-end', gap: '12px',
                margin: '15px 0', padding: '10px 15px',
                background: '#f8f9fa', borderRadius: '8px', flexWrap: 'wrap',
            }}>
                <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#666', marginBottom: '3px' }}>Goduto dal</label>
                    <input
                        type="date"
                        value={balanceDateFilter.from}
                        onChange={(e) => setBalanceDateFilter(prev => ({ ...prev, from: e.target.value }))}
                        style={{ fontSize: '0.85rem' }}
                    />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#666', marginBottom: '3px' }}>al</label>
                    <input
                        type="date"
                        value={balanceDateFilter.to}
                        onChange={(e) => setBalanceDateFilter(prev => ({ ...prev, to: e.target.value }))}
                        style={{ fontSize: '0.85rem' }}
                    />
                </div>
                {(balanceDateFilter.from || balanceDateFilter.to) && (
                    <button
                        onClick={() => setBalanceDateFilter({ from: '', to: '' })}
                        style={{
                            padding: '6px 12px', borderRadius: '6px', border: '1px solid #d1d5db',
                            backgroundColor: 'white', fontSize: '0.8rem', cursor: 'pointer', color: '#555',
                        }}
                    >
                        ✕ Reset (mostra totale)
                    </button>
                )}
                <span style={{ fontSize: '0.75rem', color: '#999', marginLeft: 'auto', alignSelf: 'center' }}>
                    {(balanceDateFilter.from || balanceDateFilter.to)
                        ? 'Il "Goduto" sotto è filtrato sul periodo selezionato.'
                        : 'Il "Goduto" sotto è il totale cumulativo di sempre.'}
                </span>
            </div>

            {searchedUserBalances ? (
                // --- VISTA MANAGER (Utente Cercato) ---
                <div className="balances-summary searched-user-balance">
                    <div className="balance-card">
                        <h4>Saldo Ferie ({searchedUserBalances.user.name})</h4>
                        <p style={{fontSize: '1.8rem'}}>
                            {searchedVacationRemaining.toFixed(2)}
                            <span style={{fontSize: '1rem', color: '#6c757d', fontWeight: 'normal'}}> giorni rimasti</span>
                        </p>
                        <div style={{fontSize: '0.8rem', color: '#555', marginTop: '5px', lineHeight: '1.4'}}>
                             <div>Attuali DB: <strong>{(searchedVacationRemaining + searchedFutureVacation).toFixed(2)}</strong></div>
                             <div style={{color: '#d32f2f'}}>future programmate: <strong>- {searchedFutureVacation}</strong></div>
                             <div style={{marginTop:'5px', borderTop:'1px solid #eee'}}>{(balanceDateFilter.from || balanceDateFilter.to) ? 'Godute nel periodo' : 'Godute (totale)'}: <strong>{searchedVacationTaken.toFixed(2)}</strong></div>
                        </div>
                    </div>

                    <div className="balance-card">
                        <h4>Permessi (ROL)</h4>
                        <p style={{fontSize: '1.8rem', color: '#0d6efd'}}>
                             {searchedRolRemaining.toFixed(2)}
                             <span style={{fontSize: '1rem', color: '#6c757d', fontWeight: 'normal'}}> ore rimaste</span>
                        </p>
                        <div style={{fontSize: '0.85rem', color: '#555', marginTop: '5px', lineHeight: '1.4'}}>
                             <div>{(balanceDateFilter.from || balanceDateFilter.to) ? 'Goduti nel periodo' : 'Goduti (totale)'}: <strong>{searchedRolTaken.toFixed(2)}</strong> h</div>
                        </div>
                    </div>

                    <div className="balance-card">
                        <h4>Malattia Annuale</h4>
                        <p>
                             {searchedUserBalances.sicknessTaken}
                             <span style={{fontSize: '1rem', color: '#6c757d'}}> giorni</span>
                        </p>
                    </div>
                    
                    {searchedUserBalances.user.leaveBalances?.hasLaw104 && (
                        <div className="balance-card">
                            <h4>Permessi 104 Goduti</h4>
                            <p>{searchedUserBalances.permessi104Taken.toFixed(1)} ore</p>
                        </div>
                    )}
                </div>
            ) : (
                // --- VISTA PERSONALE (Tu) ---
                <div className="balances-summary">
                    <div className="balance-card">
                        <h4>Saldo Ferie</h4>
                        <p style={{fontSize: '1.8rem'}}>
                            {myVacationRemaining.toFixed(2)}
                            <span style={{fontSize: '1rem', color: '#6c757d', fontWeight: 'normal'}}> giorni rimasti</span>
                        </p>
                         <div style={{fontSize: '0.85rem', color: '#555', marginTop: '5px', lineHeight: '1.4'}}>
                             <div>{(balanceDateFilter.from || balanceDateFilter.to) ? 'Godute nel periodo' : 'Godute (totale)'}: <strong>{myVacationTaken.toFixed(2)}</strong> gg</div>
                        </div>
                    </div>

                    <div className="balance-card">
                        <h4>Permessi (ROL)</h4>
                        <p style={{fontSize: '1.8rem', color: '#0d6efd'}}>
                            {myRolRemaining.toFixed(2)}
                            <span style={{fontSize: '1rem', color: '#6c757d', fontWeight: 'normal'}}> ore rimaste</span>
                        </p>
                        <div style={{fontSize: '0.85rem', color: '#555', marginTop: '5px', lineHeight: '1.4'}}>
                             <div>{(balanceDateFilter.from || balanceDateFilter.to) ? 'Goduti nel periodo' : 'Goduti (totale)'}: <strong>{myRolTaken.toFixed(2)}</strong> h</div>
                        </div>
                    </div>

                    <div className="balance-card">
                        <h4>Malattia Annuale</h4>
                        <p>
                            {personalBalances.sicknessTaken}
                            <span style={{fontSize: '1rem', color: '#6c757d'}}> giorni</span>
                        </p>
                    </div>
                    
                    {userProfile.leaveBalances?.hasLaw104 && (
                        <div className="balance-card">
                            <h4>Permessi 104 Goduti</h4>
                            <p>{personalBalances.permessi104Taken.toFixed(1)} ore</p>
                        </div>
                    )}
                </div>
            )}

            <div className="controls-bar">
                <div className="tabs-nav">
                    <button onClick={() => setActiveTab('pending')} className={activeTab === 'pending' ? 'active' : ''}>In Attesa</button>
                    <button onClick={() => setActiveTab('approved')} className={activeTab === 'approved' ? 'active' : ''}>Approvate</button>
                    <button onClick={() => setActiveTab('denied')} className={activeTab === 'denied' ? 'active' : ''}>Rifiutate</button>
                </div>
                {isManager && (
                    <div className="search-filter">
                        <input
                            type="text"
                            placeholder="Cerca per nome..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                )}
            </div>

            <div className="requests-grid">
                {isLoading ? <p>Caricamento richieste...</p> : filteredRequests.map(req => {
                    const userColor = isManager ? (allStaffData.find(s => s.id === req.userId)?.color || '#ccc') : userProfile.color;
                    const canCancel = userProfile.uid === req.userId && req.status === 'pending';
                    
                    const isMaternity = req.type === 'Maternità';
                    const statusClass = isMaternity ? 'maternita' : `status-${req.status}`;
                    const borderColor = isMaternity ? '#f48fb1' : userColor;

                    return (
                        <div key={req.id} className={`leave-card-item ${statusClass}`} style={{ borderLeftColor: borderColor }}>
                            <div className="leave-card-header">
                                <div>
                                    {isManager && <p className="user-name">{req.userName}</p>}
                                    <p className="request-type" style={{color: isMaternity ? '#d81b60' : 'inherit'}}>{req.type}</p>
                                </div>
                                <div className="status-action-group">
                                    {getStatusBadge(req.status)}
                                    {isManager && req.status === 'approved' && (
                                        <button
                                            className="delete-badge-btn"
                                            onClick={() => setDeleteModal({ isOpen: true, request: req })}
                                            title="Elimina"
                                        >
                                            🗑️
                                        </button>
                                    )}
                                    <button
                                        onClick={() => {
                                            setLeaveModalData({
                                                personId: req.userId,
                                                personName: req.userName,
                                                selectedDate: req.startDate,
                                                leaveType: req.type,
                                            });
                                        }}
                                        style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: 'white', fontSize: '0.75rem', cursor: 'pointer', color: '#2563eb', fontWeight: '600' }}
                                    >
                                        ✏️
                                    </button>
                                </div>
                            </div>
                            <div className="leave-card-body">
                                <p className="request-dates">
                                    {moment(req.startDate).format('DD MMM')} - {moment(req.endDate).format('DD MMM YYYY')}
                                    <span>
                                        ({req.totalDays} {req.totalDays > 1 ? 'giorni' : 'giorno'}
                                        {req.leaveHours && ` - ${String(req.leaveHours.h).padStart(2, '0')}:${String(req.leaveHours.m).padStart(2, '0')}h`})
                                    </span>
                                </p>
                                
                                {/* --- SEZIONE NOTE MODIFICABILI --- */}
                                {editingNoteId === req.id ? (
                                    <div className="note-editing-wrapper">
                                        <textarea
                                            className="note-textarea"
                                            value={tempNoteText}
                                            onChange={(e) => setTempNoteText(e.target.value)}
                                            onBlur={() => handleNoteBlur(req)}
                                            autoFocus
                                            placeholder="Inserisci una nota..."
                                        />
                                        {isSavingNote && <span className="saving-indicator">Salvataggio in corso...</span>}
                                    </div>
                                ) : (
                                    <div 
                                        className="note-container" 
                                        onClick={() => handleStartEditingNote(req)}
                                        title="Clicca per modificare"
                                    >
                                        <p className="request-notes">
                                            {req.notes || 'Nessuna nota.'}
                                            <span className="note-edit-icon">✏️</span>
                                        </p>
                                    </div>
                                )}
                                {/* --------------------------------- */}

                            </div>
                            {(isManager && req.status === 'pending') || canCancel ? (
                                <div className="leave-card-footer">
                                    {isManager && req.status === 'pending' && (
                                        <>
                                            <button className="approve-btn" onClick={() => handleUpdateRequestStatus(req, 'approved')}>Approva</button>
                                            <button className="deny-btn" onClick={() => handleUpdateRequestStatus(req, 'denied')}>Rifiuta</button>
                                        </>
                                    )}
                                    {canCancel && (
                                        <button className="cancel-request-btn" onClick={() => setCancelModal({ isOpen: true, request: req })}>Annulla Richiesta</button>
                                    )}
                                </div>
                            ) : null}
                        </div>
                    );
                })}
                {!isLoading && filteredRequests.length === 0 && (
                    <p className="no-requests-message">Nessuna richiesta trovata in questa categoria.</p>
                )}
            </div>
        </div>
    );
}

export default LeaveManagementPage;